import { z } from 'zod';
import { phaseSchema } from './planning';
import { dimensionSchema } from './squad';

/**
 * An optional field has to accept `null`, not only `undefined`.
 *
 * Every one of these schemas validates a body the screen built out of a row this
 * same API returned, and a column with nothing in it comes back as `null`. A
 * schema that takes `undefined` and refuses `null` therefore rejects its own
 * output: the first save of a match squad worked, and every save after a reload
 * failed with a validation error the screen never showed. Both mean "nothing
 * here", and both arrive as nothing.
 */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    // `.optional()` last: a transform hides the key's optionality from zod, and
    // without it every caller would have to spell out every field it is leaving
    // out.
    .transform((v) => (v == null || v === '' ? undefined : v))
    .optional();

const optionalNumber = (schema: z.ZodNumber) =>
  schema
    .nullish()
    .transform((v) => v ?? undefined)
    .optional();

export const objectiveLevelSchema = z.enum([
  'season', 'development', 'player', 'cycle', 'microcycle', 'session', 'exercise',
]);

export const objectiveStatusSchema = z.enum(['open', 'active', 'achieved', 'dropped']);

export const objectiveInputSchema = z.object({
  teamId: z.string().min(1).max(64).optional(),
  seasonId: z.string().min(1).max(64).optional(),
  playerId: z.string().min(1).max(64).optional(),
  level: objectiveLevelSchema,
  parentObjectiveId: z.string().min(1).max(64).optional(),
  dimension: dimensionSchema.optional(),
  emphasis: z.enum(['primary', 'secondary']).default('primary'),
  title: z.string().trim().min(1).max(300),
  detail: optionalText(2000),
  status: objectiveStatusSchema.default('open'),
});

export const matchDetailSchema = z.object({
  formation: optionalText(20),
  gamePlan: optionalText(4000),
  oppositionNotes: optionalText(4000),
  setPieces: optionalText(4000),
  /** Written before kick-off, read after. */
  whatWeWantToSee: optionalText(2000),
  goalsFor: optionalNumber(z.number().int().min(0).max(50)),
  goalsAgainst: optionalNumber(z.number().int().min(0).max(50)),
  /**
   * The call-up: what a player needs in order to turn up in the right place at
   * the right time with the right bag. Kick-off is on the fixture; none of this
   * is, and a call-up without it is a list of names.
   */
  meetAt: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullish()
    .transform((v) => v ?? undefined)
    .optional(),
  meetPlace: optionalText(160),
  kit: optionalText(240),
  callUpNote: optionalText(400),
});

export const matchSquadEntrySchema = z.object({
  playerId: z.string().min(1).max(64),
  role: z.enum(['starter', 'bench', 'unavailable']),
  positionKey: optionalText(12),
  minutesPlayed: optionalNumber(z.number().min(0).max(150)),
  onMinute: optionalNumber(z.number().int().min(0).max(150)),
  offMinute: optionalNumber(z.number().int().min(0).max(150)),
  assignment: optionalText(1000),
  note: optionalText(500),
});

/**
 * The minutes plan, saved whole.
 *
 * Replace rather than merge, the same as the squad: a period removed on screen
 * has to be a period removed in the database, and a diff of free-form time
 * windows is a way of keeping a period nobody can see.
 */
export const matchPeriodSchema = z.object({
  fromMinute: z.number().int().min(0).max(200),
  toMinute: z.number().int().min(0).max(200),
  formation: optionalText(20),
  label: optionalText(60),
  note: optionalText(300),
  players: z
    .array(
      z.object({
        playerId: z.string().min(1).max(64),
        positionKey: optionalText(12),
      }),
    )
    .max(30)
    .default([]),
});

export const matchPlanSchema = z.object({
  periods: z.array(matchPeriodSchema).max(12).default([]),
});

/**
 * Who turned up, which is not who was picked.
 *
 * Five states, and none of them means "not selected": that is a different fact,
 * it lives on the squad, and conflating them would make the coach's decision and
 * the player's behaviour the same word.
 */
export const matchAttendanceStatusSchema = z.enum([
  'present',
  'late',
  'absent',
  'excused',
  'injured',
]);

export const matchAttendanceSchema = z.object({
  entries: z
    .array(
      z.object({
        playerId: z.string().min(1).max(64),
        status: matchAttendanceStatusSchema,
        note: optionalText(300),
      }),
    )
    .max(60)
    .default([]),
});

export const matchSquadSchema = z.object({
  entries: z.array(matchSquadEntrySchema).max(60).default([]),
});

export const matchAnalysisSchema = z.object({
  general: optionalText(4000),
  withBall: optionalText(4000),
  withoutBall: optionalText(4000),
  attackingTransition: optionalText(4000),
  defensiveTransition: optionalText(4000),
  setPieces: optionalText(4000),
  technical: optionalText(4000),
  tactical: optionalText(4000),
  physical: optionalText(4000),
  mental: optionalText(4000),
  social: optionalText(4000),
  whatWorked: optionalText(4000),
  whatFailed: optionalText(4000),
});

export const matchProblemInputSchema = z.object({
  phase: phaseSchema.optional(),
  dimension: dimensionSchema.optional(),
  title: z.string().trim().min(1).max(300),
  detail: optionalText(2000),
  priority: z.number().int().min(1).max(20).default(1),
});

/** Turning a match problem into next week's objective. */
export const promoteProblemSchema = z.object({
  teamId: z.string().min(1).max(64),
  seasonId: z.string().min(1).max(64).optional(),
  /** Defaults to the problem's own wording; a coach may sharpen it. */
  title: z.string().trim().min(1).max(300).optional(),
  detail: optionalText(2000),
  level: objectiveLevelSchema.default('microcycle'),
});

export type ObjectiveInput = z.infer<typeof objectiveInputSchema>;
export type MatchDetailInput = z.infer<typeof matchDetailSchema>;
export type MatchSquadInput = z.infer<typeof matchSquadSchema>;
export type MatchPlanInput = z.infer<typeof matchPlanSchema>;
export type MatchAttendanceInput = z.infer<typeof matchAttendanceSchema>;
export type MatchAttendanceStatus = z.infer<typeof matchAttendanceStatusSchema>;
export type MatchPeriodInput = z.infer<typeof matchPeriodSchema>;
export type MatchSquadEntry = z.infer<typeof matchSquadEntrySchema>;
export type MatchAnalysisInput = z.infer<typeof matchAnalysisSchema>;
export type MatchProblemInput = z.infer<typeof matchProblemInputSchema>;
export type PromoteProblemInput = z.infer<typeof promoteProblemSchema>;

export interface ObjectiveRecord {
  id: string;
  level: string;
  parentObjectiveId: string | null;
  dimension: string | null;
  emphasis: string;
  title: string;
  detail: string | null;
  sourceEntity: string | null;
  sourceId: string | null;
  status: string;
  teamId: string | null;
  playerId: string | null;
}

export interface MatchProblemRecord {
  id: string;
  eventId: string;
  phase: string | null;
  dimension: string | null;
  title: string;
  detail: string | null;
  priority: number;
  objectiveId: string | null;
}

export interface MatchRecord {
  event: {
    id: string;
    onDate: string;
    opponent: string | null;
    venue: string | null;
    competition: string | null;
    status: string;
    kind: string;
    startsAt: string | null;
  };
  /**
   * The stored row, which carries one field the input schema does not: how hard
   * the match was. That arrives with the squad, because it is rated at the same
   * moment the minutes are.
   */
  detail: (MatchDetailInput & { matchRpe?: number | null }) | null;
  analysis: MatchAnalysisInput | null;
  squad: Array<
    MatchSquadEntry & { firstName: string; lastName: string; shirtNumber: number | null }
  >;
  problems: MatchProblemRecord[];
}
